/**
 * Well Servicing helpers (WS-1): who may press what, how a refusal reads, the
 * well identifier by country, and the rig / activity-code labels.
 *
 * The identifier label (UWI in CA, API number in US) and the mast-rating unit
 * come from the tenant meta, never from a literal. When meta did not arrive
 * the label is the neutral fallback, not a guess.
 */
import type { WsMeta } from '../types/well-servicing';
import { fmtQty } from './display-labels';
import { formatCalendarDate } from './calendar-date';

// ─── Who may press what (mirrors fieldiq-api lib/ws-roles.ts) ────────────────
//
// For hiding a button, not for enforcement. A 403 that still arrives is shown
// as a refusal, never as success.

export const WS_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'field_worker', 'logistics'] as const;
export const WS_ADMIN_ROLES = ['admin', 'office_staff'] as const;
export const WS_WELL_WRITE_ROLES = ['admin', 'office_staff', 'supervisor'] as const;
export const WS_CREW_WRITE_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
/** Settings, customer settings, rig create / edit / delete, well delete, activity codes. */
export const canAdminWs = inList(WS_ADMIN_ROLES);
/** Well create / edit. */
export const canWriteWsWells = inList(WS_WELL_WRITE_ROLES);
/** Crew create / edit / delete and crew members. */
export const canWriteWsCrews = inList(WS_CREW_WRITE_ROLES);

// WS-2 (fieldiq-api lib/ws-roles.ts, #1282).
export const WS_DISPATCH_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
export const WS_RENT_IN_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'logistics'] as const;
export const WS_TOUR_WRITE_ROLES = ['admin', 'office_staff', 'supervisor', 'field_worker'] as const;
export const WS_TOUR_APPROVE_ROLES = ['admin', 'office_staff'] as const;
/** Job create / edit / move / delete, its equipment, rent-ins and BOP stack. */
export const canDispatchWs = inList(WS_DISPATCH_ROLES);
/** The rent-in cost keys (rate, unit, billable, PO) reach these roles only. */
export const canSeeWsRentInCost = inList(WS_RENT_IN_READ_ROLES);
/** Open, edit and submit a tour sheet. */
export const canWriteWsTours = inList(WS_TOUR_WRITE_ROLES);
/** Approve, return to draft, delete a draft. */
export const canApproveWsTours = inList(WS_TOUR_APPROVE_ROLES);

// ─── Refusals ────────────────────────────────────────────────────────────────

export interface WsApiError {
  status: number | null;
  code: string | null;
  message: string | null;
  extra: Record<string, unknown>;
}

export function wsApiError(err: unknown): WsApiError {
  const r = (err as { response?: { status?: unknown; data?: { error?: unknown; code?: unknown } } })?.response;
  const d = r?.data;
  return {
    status: typeof r?.status === 'number' ? r.status : null,
    code: typeof d?.code === 'string' ? d.code : null,
    message: typeof d?.error === 'string' && d.error ? d.error : null,
    extra: d && typeof d === 'object' ? (d as Record<string, unknown>) : {},
  };
}

export const WS_PERMISSION_REFUSAL = 'You do not have permission to do this. Ask an admin or office staff.';

/**
 * A write's error as one plain sentence. A 403 is a permission refusal, a bare
 * 404 reads as gone; every coded WS-1 refusal (duplicate_well,
 * duplicate_rig_number, rig_has_crews, already_on_crew, …) carries a sentence
 * from the API that names the record, so it is shown as sent; else `fallback`.
 */
export function wsRefusal(err: unknown, fallback: string): string {
  const e = wsApiError(err);
  if (e.status === 403) return WS_PERMISSION_REFUSAL;
  if (e.status === 404 && !e.code) return 'That record no longer exists. It may have been deleted.';
  return e.message ?? fallback;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * The 409 duplicate_well: the API's sentence names the existing well
 * ("Well "X" already has this UWI."), and `existingWellId` lets the form link
 * to it.
 */
export function duplicateWell(err: unknown): { message: string; existingWellId: string | null } | null {
  const e = wsApiError(err);
  if (e.status !== 409 || e.code !== 'duplicate_well') return null;
  const what = e.extra.field === 'api_number' ? 'API number' : 'UWI';
  return { message: e.message ?? `Another well already has this ${what}.`, existingWellId: str(e.extra.existingWellId) };
}

/** The 409 rig_exists_for_equipment / duplicate_rig_number: the rig it names. */
export function duplicateRig(err: unknown): { message: string; existingRigId: string | null } | null {
  const e = wsApiError(err);
  if (e.status !== 409 || (e.code !== 'rig_exists_for_equipment' && e.code !== 'duplicate_rig_number' && e.code !== 'duplicate_rig')) return null;
  return { message: e.message ?? 'Another rig already uses this equipment or rig number.', existingRigId: str(e.extra.existingRigId) };
}

// ─── Identifier by country ───────────────────────────────────────────────────

export const wsIsUs = (meta: Pick<WsMeta, 'country'> | null | undefined) => meta?.country === 'US';
export const wellIdLabel = (meta: Pick<WsMeta, 'country'> | null | undefined) => (wsIsUs(meta) ? 'API number' : 'UWI');
export const wellIdOf = (
  well: { uwi: string | null; apiNumber: string | null },
  meta: Pick<WsMeta, 'country'> | null | undefined,
): string | null => (wsIsUs(meta) ? well.apiNumber : well.uwi);

/**
 * The rig's registry number: the CAOEC number in Canada; a US contractor has
 * no CAOEC, so the same field is its own contractor number.
 */
export const rigRegistryLabel = (meta: Pick<WsMeta, 'country'> | null | undefined) =>
  wsIsUs(meta) ? 'Contractor rig number' : 'CAOEC rig number';

// ─── Display ─────────────────────────────────────────────────────────────────

/** A number as given, grouped; absent → `—`. A real 0 is `0`. */
export const fmtWsNumber = (n: number | null | undefined): string => fmtQty(n, 6);

/** `800 ppm`; absent → `—` with no unit after it. */
export function withUnit(n: number | null | undefined, unit: string | null | undefined): string {
  const v = fmtWsNumber(n);
  if (v === '—') return v;
  return unit ? `${v} ${unit}` : v;
}

/** `45,000 daN (450 kN)` — as entered, with the API's kN copy when it differs; absent → `—`. */
export function mastRatingText(rig: { mastRating: number | null; mastRatingUnit: string | null; mastRatingKn: number | null }): string {
  const v = withUnit(rig.mastRating, rig.mastRatingUnit);
  if (v === '—') return v;
  if (rig.mastRatingUnit === 'kN' || rig.mastRatingKn == null) return v;
  return `${v} (${fmtQty(rig.mastRatingKn, 1)} kN)`;
}

/** The label the API's catalog gives a value; the raw value if the catalog lacks it; `—` when absent. */
export function catalogLabel(list: readonly { value: string; label: string }[] | undefined, value: string | null | undefined): string {
  if (!value) return '—';
  return list?.find((e) => e.value === value)?.label ?? value;
}

export const blankToNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());
export const toInput = (v: number | string | null | undefined): string => (v === null || v === undefined ? '' : String(v));

export interface WsSkin {
  label: string;
  fill: string;
  rail: string;
}

/** A well has no status: its rail claims no colour. Sour is a flag, drawn as a badge. */
export const WS_RAIL = 'border-l-transparent';
export const WS_SOUR_SKIN: WsSkin = { label: 'Sour', fill: 'bg-fi-signal-red', rail: 'border-l-transparent' };

/** The activity code's billing class: what a ticket line on this code bills as. */
export const WS_BILLING_CLASS_SKIN: Record<string, WsSkin> = {
  rig_rate: { label: 'Rig rate', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  standby: { label: 'Standby', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  travel: { label: 'Travel', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  not_billable: { label: 'Not billable', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
const WS_UNKNOWN_SKIN: WsSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

/** The skin for a billing class; the label is the API's when it sent one. */
export function wsBillingSkin(value: string | null | undefined, apiLabel?: string | null): WsSkin {
  const base = value && Object.prototype.hasOwnProperty.call(WS_BILLING_CLASS_SKIN, value) ? WS_BILLING_CLASS_SKIN[value] : WS_UNKNOWN_SKIN;
  return apiLabel ? { ...base, label: apiLabel } : value && base === WS_UNKNOWN_SKIN ? { ...base, label: value } : base;
}

export const WS_RETIRED_SKIN: WsSkin = { label: 'Retired', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' };

/** The activity code as the API stores it: upper case, letters, digits, `-` and `_`, max 20. */
export const WS_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]*$/;
export const normalizeWsCode = (s: string): string => s.trim().toUpperCase();

/** 'HH:MM', 24-hour (mirrors the API's tour_start_time check). */
export const WS_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// ═══ WS-2 — jobs and tour sheets ═════════════════════════════════════════════

/** A calendar day ('YYYY-MM-DD') as `Oct 31, 2026`; absent → `—`. */
export const wsDay = (value: string | null | undefined): string => formatCalendarDate(value) ?? '—';

/** Hours as the API computed them, one decimal kept as sent; absent → `—`. A real 0 is `0 h`. */
export function wsHours(n: number | null | undefined): string {
  const v = fmtQty(n, 2);
  return v === '—' ? v : `${v} h`;
}

const UNKNOWN_SKIN: WsSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
const pick = (map: Record<string, WsSkin>, v: string | null | undefined, apiLabel?: string | null): WsSkin => {
  const base = v && Object.prototype.hasOwnProperty.call(map, v) ? map[v] : v ? { ...UNKNOWN_SKIN, label: v } : UNKNOWN_SKIN;
  return apiLabel ? { ...base, label: apiLabel } : base;
};

/** One hue per meaning: draft steel, scheduled blue, the three on-site stages amber, completed green, cancelled red. */
export const WS_JOB_STATUS_SKIN: Record<string, WsSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  scheduled: { label: 'Scheduled', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  moving_in: { label: 'Moving in / rigging up', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  in_progress: { label: 'In progress', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  rigging_out: { label: 'Rigging out', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  completed: { label: 'Completed', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const wsJobStatusSkin = (status: string | null | undefined, apiLabel?: string | null): WsSkin =>
  pick(WS_JOB_STATUS_SKIN, status, apiLabel);

/** Draft steel, submitted blue (waiting on the office), approved green. */
export const WS_TOUR_STATUS_SKIN: Record<string, WsSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  submitted: { label: 'Submitted', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  office_approved: { label: 'Approved by the office', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
};
export const wsTourStatusSkin = (status: string | null | undefined, apiLabel?: string | null): WsSkin =>
  pick(WS_TOUR_STATUS_SKIN, status, apiLabel);

export const WS_RENT_STATUS_SKIN: Record<string, WsSkin> = {
  upcoming: { label: 'Upcoming', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  on_rent: { label: 'On rent', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  off_rent: { label: 'Off rent', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const wsRentStatusSkin = (status: string | null | undefined): WsSkin => pick(WS_RENT_STATUS_SKIN, status);

/** Open = draft through rigging_out (lib/ws-jobs.ts WS_OPEN_STATUSES); takes edits. */
export const wsJobOpen = (status: string | null | undefined) =>
  status === 'draft' || status === 'scheduled' || status === 'moving_in' || status === 'in_progress' || status === 'rigging_out';
/** A sheet can be opened on scheduled through rigging_out (WS_TOURABLE_STATUSES). */
export const wsJobTourable = (status: string | null | undefined) => wsJobOpen(status) && status !== 'draft';
/** Only a draft or scheduled job can be deleted; the API decides the sheets half. */
export const wsJobDeletable = (status: string | null | undefined) => status === 'draft' || status === 'scheduled';

/** `10:00 to 14:30` for a gap or a window. */
export const wsSpan = (from: string | null | undefined, to: string | null | undefined): string =>
  from && to ? `${from} to ${to}` : '—';

const xs = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * WS-2 refusal codes as one plain sentence. Codes the map lacks fall back to
 * the API's own sentence (every WS-2 refusal sends one), then to `fallback`.
 */
const WS_REFUSALS: Record<string, (x: Record<string, unknown>) => string> = {
  invalid_transition: (x) => {
    const allowed = Array.isArray(x.allowed) ? (x.allowed as string[]) : [];
    return allowed.length
      ? `This job cannot move to that status. From here it can go to: ${allowed.map((a) => wsJobStatusSkin(a).label).join(', ')}.`
      : 'This job cannot move to that status from where it is.';
  },
  job_closed: () => 'This job is completed or cancelled, so it can no longer be changed.',
  job_not_deletable: () => 'Only a draft or scheduled job can be deleted. Cancel this one instead.',
  job_has_tours: () => 'This job has tour sheets, so it cannot be deleted. Cancel it instead.',
  job_has_draft_tours: (x) =>
    `The tour sheet for ${xs(x.workDate) ? wsDay(xs(x.workDate)) : 'a day'} is still a draft. Submit or delete it first.`,
  job_not_scheduled: () => 'This job is still a draft. Schedule it before opening a tour sheet.',
  job_has_no_rig: () => 'This job has no rig. Name the rig before opening a tour sheet.',
  rig_has_tours: () => 'This job has tour sheets on its rig, so the rig cannot be changed.',
  well_customer_mismatch: () => 'That well belongs to a different customer.',
  job_number_contended: () => 'Another job took that number at the same moment. Try again.',
  already_on_job: () => 'That unit is already on this job.',
  unit_has_hours: () => 'This unit has hours on a tour sheet, so it cannot be taken off the job. Set its off date instead.',
  unit_hours_outside_dates: (x) =>
    `This unit has hours on ${xs(x.workDate) ? wsDay(xs(x.workDate)) : 'a day'} outside the new dates. Keep that day inside them.`,
  invalid_rent_dates: () => 'The off-rent date cannot be before the on-rent date.',
  rent_in_has_hours: () => 'This rented-in unit has hours on a tour sheet. Set its off-rent date instead of removing it.',
  rent_in_hours_outside_dates: (x) =>
    `This rented-in unit has hours on ${xs(x.workDate) ? wsDay(xs(x.workDate)) : 'a day'} outside the new rent dates. Keep that day inside them.`,
  tour_submitted: () => 'This tour sheet is submitted, so it can no longer be changed. The office can return it to draft.',
  tour_approved: () => 'This tour sheet is approved by the office and locked.',
  tour_not_submitted: () => 'Only a submitted tour sheet can be approved or returned.',
  tour_has_no_crew: () => 'Add the crew before submitting the tour sheet.',
  activities_do_not_cover_tour: (x) => {
    const gaps = Array.isArray(x.gaps) ? (x.gaps as { from?: string; to?: string }[]) : [];
    const list = gaps.map((g) => wsSpan(g.from, g.to)).filter((g) => g !== '—');
    return list.length
      ? `The activities do not cover the whole sheet. Fill these gaps: ${list.join(', ')}.`
      : 'The activities do not cover the whole sheet. Fill the gaps first.';
  },
  future_day: (x) => `A tour sheet cannot be for a future day. Today is ${xs(x.today) ? wsDay(xs(x.today)) : 'earlier'}.`,
  work_date_fixed: () => 'A sheet\'s day does not change. Delete it and open one for the other day.',
  duplicate_crew: () => 'The same person is listed twice. Remove one.',
  duplicate_unit: () => 'The same unit is listed twice. Remove one of the rows.',
  equipment_not_on_job: () => 'That unit is not on this job. Add it to the job\'s equipment first.',
  rent_in_not_on_job: () => 'That rented-in unit belongs to another job.',
  rent_in_not_on_rent: () => 'That rented-in unit was not on rent that day.',
  meter_owned_only: () => 'An hour-meter reading can only be recorded for an owned unit.',
  not_on_crew: () => 'You are not on this job\'s crew, so you cannot open or change its tour sheets.',
  // WS-3 (fieldiq-api#1286): string events, tallies, pressure-control links.
  correction_reason_required: () => 'This sheet is submitted, so a new event on it is a correction. Say why it is being corrected.',
  event_is_correction: () => 'This event corrects another, so it cannot be removed. The office can void it instead.',
  event_voided: () => 'That event is already voided.',
  event_other_job: () => 'The event being corrected belongs to another job.',
  equipment_needs_owner_ours: () => 'Only our own equipment can be named on an event. Set the owner to Ours.',
  depth_order: () => 'The bottom depth must be at or below the top depth.',
  tally_voided: () => 'This tally is voided.',
  lines_required: () => 'Changing the tally\'s length unit re-reads every joint. Send the joints with it.',
  duplicate_line_no: (x) => `Joint ${typeof x.lineNo === 'number' ? x.lineNo : ''} is listed twice.`.replace('  ', ' '),
  test_outside_tour: () => 'Only pressure tests done during this sheet can be linked to it.',
  drill_outside_tour: () => 'Only drills done during this sheet can be linked to it.',
  test_linked_elsewhere: () => 'That pressure test is already on another tour sheet.',
  drill_linked_elsewhere: () => 'That drill is already on another tour sheet.',
};

export function wsJobRefusal(err: unknown, fallback: string): string {
  const e = wsApiError(err);
  if (e.status === 403 && e.code !== 'not_on_crew') return WS_PERMISSION_REFUSAL;
  if (e.status === 404 && !e.code) return 'That record no longer exists. It may have been deleted.';
  const fn = e.code ? WS_REFUSALS[e.code] : undefined;
  // The API's own sentence names the exact rows for these; prefer it.
  if (e.code && e.message && ['activity_outside_tour', 'activities_overlap', 'unit_hours_exceed_tour', 'invalid_time',
    'time_does_not_exist', 'activity_code_retired'].includes(e.code)) return e.message;
  return fn ? fn(e.extra) : e.message ?? fallback;
}

// ─── Field-recorded BOP tests and drills (fieldiq-api#1324, 0605) ────────────
//
// The operator's rule (2026-11-02): "Field workers (rig manager, driller) can
// record BOP tests and drills, attached to their own draft tour sheet; office
// approves them with the sheet. Floorhands view only." The API decides; these
// only choose which buttons to draw.

/** The tour-sheet positions that may record a BOP test or drill in the field (the API's WS_BOP_RECORDER_POSITIONS). */
export const WS_BOP_RECORDER_POSITIONS = ['rig_manager', 'driller'] as const;

/**
 * Whether `userId` may record a BOP test or drill on this sheet: on its crew as
 * rig manager or driller, and the sheet a draft (one crew row per hand per
 * sheet, uq_ws_tour_crew_tour_user).
 */
export function canRecordWsBop(
  tour: { status: string; locked?: boolean; crew: Array<{ user_id: string; position: string }> } | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!tour || !userId || tour.status !== 'draft' || tour.locked) return false;
  return tour.crew.some((c) => c.user_id === userId && (WS_BOP_RECORDER_POSITIONS as readonly string[]).includes(c.position));
}

/**
 * A refused field BOP write in the API's own sentence. Unlike wsJobRefusal a 403
 * here is not the generic permission line: position_cannot_record names who can
 * record (the rig manager or the driller on the sheet), which is what the hand
 * needs to read.
 */
export function wsBopRefusal(err: unknown, fallback: string): string {
  const e = wsApiError(err);
  if (e.message) return e.message;
  return wsJobRefusal(err, fallback);
}

/**
 * The default instant for a record made on a sheet: now, when now is inside the
 * sheet's window, else the sheet's start — the API refuses an instant outside it.
 */
export function wsBopDefaultInstant(startAt: string | null | undefined, endAt: string | null | undefined, now: Date = new Date()): string {
  const s = startAt ? Date.parse(startAt) : NaN;
  const e = endAt ? Date.parse(endAt) : NaN;
  const n = now.getTime();
  if (Number.isFinite(s) && Number.isFinite(e) && (n < s || n > e)) return new Date(s).toISOString();
  return now.toISOString();
}

/** The 409 tour_exists: the sheet already open for that rig and day. */
export function existingTour(err: unknown): { id: string; jobNumber: string | null; message: string } | null {
  const e = wsApiError(err);
  if (e.status !== 409 || e.code !== 'tour_exists' || !xs(e.extra.existingId)) return null;
  return { id: xs(e.extra.existingId) as string, jobNumber: xs(e.extra.jobNumber), message: e.message ?? 'This rig already has a tour sheet for that day.' };
}

/** The 409 bop_stack_refused: each reason the shared guard gave. */
export function bopRefusals(err: unknown): string[] | null {
  const e = wsApiError(err);
  if (e.code !== 'bop_stack_refused') return null;
  const f = Array.isArray(e.extra.failures) ? (e.extra.failures as { error?: unknown }[]) : [];
  const lines = f.map((x) => xs(x.error)).filter((x): x is string => !!x);
  return lines.length ? lines : [e.message ?? 'The BOP stack was refused.'];
}

// ═══ WS-3 — string events, tallies, installed string, well report ═══════════

/**
 * Who may add a string event to a sheet, and as what (mirrors
 * lib/ws-string-records.ts addStringEvent):
 *   - a DRAFT sheet: the tour roles add ordinary events;
 *   - a submitted or approved sheet: only the office (admin, office staff),
 *     and only as a CORRECTION with a reason;
 *   - anyone else: nothing.
 * For hiding a button; the API is the gate.
 */
export function wsStringAddMode(tourStatus: string | null | undefined, role: string | null | undefined): 'draft' | 'correction' | null {
  if (tourStatus === 'draft') return canWriteWsTours(role) ? 'draft' : null;
  if (tourStatus === 'submitted' || tourStatus === 'office_approved') return canApproveWsTours(role) ? 'correction' : null;
  return null;
}

/** A draft sheet's event, not a correction, not voided: it is not yet a record and can be removed. */
export const wsEventRemovable = (
  e: { tourStatus: string; voided: boolean; correctsEventId: string | null },
  role: string | null | undefined,
): boolean => e.tourStatus === 'draft' && !e.voided && !e.correctsEventId && canWriteWsTours(role);

/** The office voids any live event, with a reason. */
export const wsEventVoidable = (e: { voided: boolean }, role: string | null | undefined): boolean => !e.voided && canApproveWsTours(role);

/** `1,234.5 m` from an entered { value, unit }; absent → `—`. */
export const wsEntered = (v: { value: number; unit: string } | null | undefined): string => (v ? withUnit(v.value, v.unit) : '—');

/** `1,200 to 1,250 m` (either end may be absent); both absent → `—`. */
export function wsDepthRange(top: { value: number; unit: string } | null | undefined, bottom: { value: number; unit: string } | null | undefined): string {
  if (!top && !bottom) return '—';
  const unit = (top ?? bottom)!.unit;
  return `${top ? fmtWsNumber(top.value) : '—'} to ${bottom ? fmtWsNumber(bottom.value) : '—'} ${unit}`;
}

/** What the item is: `73 mm · J-55 · SN 12-34`, from the fields entered; none → `—`. */
export function wsItemIdentity(e: { description?: string | null; size: string | null; grade: string | null; serialNumber?: string | null }): string {
  const parts = [e.description, e.size, e.grade, e.serialNumber ? `SN ${e.serialNumber}` : null].filter((p): p is string => !!p && p.trim() !== '');
  return parts.length ? parts.join(' · ') : '—';
}

/** The event form as typed. Every field a string, so a blank stays absent rather than becoming 0. */
export interface WsEventForm {
  action: string;
  componentKind: string;
  description: string;
  size: string;
  grade: string;
  serialNumber: string;
  quantity: string;
  length: string;
  topDepth: string;
  bottomDepth: string;
  unit: string;
  condition: string;
  owner: string;
  notes: string;
  correctionReason: string;
  correctsEventId: string;
}

export const emptyWsEventForm = (unit: string): WsEventForm => ({
  action: '', componentKind: '', description: '', size: '', grade: '', serialNumber: '', quantity: '1', length: '',
  topDepth: '', bottomDepth: '', unit, condition: '', owner: 'operator', notes: '', correctionReason: '', correctsEventId: '',
});

const optNum = (s: string): number | null | 'bad' => {
  if (s.trim() === '') return null;
  const n = Number(s.trim());
  return Number.isFinite(n) && n >= 0 ? n : 'bad';
};

/**
 * The event body from the form, or the sentence that stops it. On a
 * correction the reason is REQUIRED here as well as by the API — a correction
 * with no reason is the one write this screen must never send as if it were an
 * ordinary event.
 */
export function wsEventBody(f: WsEventForm, mode: 'draft' | 'correction', opts: { field?: boolean } = {}): { body: import('../types/well-servicing').WsStringEventWrite } | { error: string } {
  if (!f.action) return { error: 'Choose what happened.' };
  if (!f.componentKind) return { error: 'Choose the component.' };
  const q = Number(f.quantity.trim());
  if (!Number.isInteger(q) || q < 1) return { error: 'The quantity is a whole number from 1.' };
  const length = optNum(f.length);
  const top = optNum(f.topDepth);
  const bottom = optNum(f.bottomDepth);
  if (length === 'bad' || top === 'bad' || bottom === 'bad') return { error: 'Lengths and depths are numbers from 0.' };
  if (length === 0) return { error: 'The length must be greater than 0.' };
  if (top !== null && bottom !== null && bottom < top) return { error: 'The bottom depth must be at or below the top depth.' };
  const reason = f.correctionReason.trim();
  if (mode === 'correction' && !reason) return { error: 'This sheet is submitted. Say why this correction is being made.' };
  const txt = (s: string) => (s.trim() === '' ? null : s.trim());
  const body: import('../types/well-servicing').WsStringEventWrite = {
    action: f.action,
    component_kind: f.componentKind,
    quantity: q,
    description: txt(f.description),
    size: txt(f.size),
    grade: txt(f.grade),
    serial_number: txt(f.serialNumber),
    condition: txt(f.condition),
    owner: f.owner || 'operator',
    notes: txt(f.notes),
  };
  if (length !== null) {
    body.length = length;
    body.length_unit = f.unit;
  }
  if (top !== null || bottom !== null) {
    body.top_depth = top;
    body.bottom_depth = bottom;
    body.depth_unit = f.unit;
  }
  if (mode === 'correction' && !opts.field) {
    body.correction_reason = reason;
    if (f.correctsEventId) body.corrects_event_id = f.correctsEventId;
  }
  return { body };
}

/** A tally's joints as typed, one row each. */
export interface WsTallyLineForm {
  length: string;
  serialNumber: string;
  condition: string;
}

/** The lines body (numbered in order), or the sentence naming the first bad joint. Blank rows are dropped. */
export function wsTallyLinesBody(rows: WsTallyLineForm[]): { lines: import('../types/well-servicing').WsTallyLineWrite[] } | { error: string } {
  const kept = rows.filter((r) => r.length.trim() !== '' || r.serialNumber.trim() !== '' || r.condition !== '');
  const lines: import('../types/well-servicing').WsTallyLineWrite[] = [];
  for (let i = 0; i < kept.length; i += 1) {
    const n = Number(kept[i].length.trim());
    if (kept[i].length.trim() === '' || !Number.isFinite(n) || n <= 0 || n > 100) {
      return { error: `Joint ${i + 1}: the length is a number greater than 0 and at most 100.` };
    }
    lines.push({
      line_no: i + 1,
      length: n,
      serial_number: kept[i].serialNumber.trim() || null,
      condition: kept[i].condition || null,
    });
  }
  return { lines };
}

/** Draft steel, issued green. */
export const WS_REPORT_STATUS_SKIN: Record<string, WsSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  issued: { label: 'Issued', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
};
export const wsReportStatusSkin = (status: string | null | undefined): WsSkin => pick(WS_REPORT_STATUS_SKIN, status);

/** Voided events and tallies: red, the label says so. */
export const WS_VOIDED_SKIN: WsSkin = { label: 'Voided', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' };
export const WS_CORRECTION_SKIN: WsSkin = { label: 'Correction', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' };

const WS_REPORT_REFUSALS: Record<string, () => string> = {
  report_exists: () => 'This job already has a draft report. Edit that one.',
  report_issued: () => 'This report is issued and can no longer be changed. Start a new draft for a revision.',
  already_issued: () => 'This report is already issued.',
  job_not_completed: () => 'The end-of-job report is issued once the job is completed. Complete the job first.',
  job_not_scheduled: () => 'This job is a draft, so it has nothing to report yet.',
  job_cancelled: () => 'This job is cancelled.',
  signatory_unknown: () => 'Your user account has no name to sign the report with. Add your name to your profile first.',
  report_changed: () => 'The report changed while it was being issued. Try again.',
};

/** A report write's refusal as one sentence. */
export function wsReportRefusal(err: unknown, fallback: string): string {
  const e = wsApiError(err);
  if (e.status === 403) return WS_PERMISSION_REFUSAL;
  if (e.status === 404 && !e.code) return 'That report no longer exists. It may have been deleted.';
  const fn = e.code ? WS_REPORT_REFUSALS[e.code] : undefined;
  return fn ? fn() : e.message ?? fallback;
}

/** A pressure-control record can be ticked for this sheet unless another sheet already holds it (one sheet per record). */
export const wsLinkSelectable = (c: { linkedTourId: string | null; onThisTour: boolean }): boolean => !c.linkedTourId || c.onThisTour;

/** The ids a PUT sends: the full set ticked, in a stable order. */
export const wsLinkSet = (ticked: Set<string>): string[] => Array.from(ticked).sort();
